import {newGpsObservation} from "./position-status.ts";
import { LocalApiError, type LocalApiClient, type LocalSnapshot } from "./local-api.ts";

export type PositionProgress = "sending" | "waiting" | "waitingNoFix" | "gps" | "noFix" | "noReport" | "cancelled" | "failed";
export type PositionFollowupOptions = {
  intervalMs?: number; maxChecks?: number; now?:()=>number;
  isCurrent: () => boolean;
  pause: (milliseconds: number) => Promise<void>;
  onUpdate: (snapshot: LocalSnapshot, progress: PositionProgress) => void;
  readSnapshot?: (deviceId: string) => Promise<LocalSnapshot>;
  onRequested?: (observation: { baselineId: number; requestedAt: number }) => void;
};

// A new database row is an observation, not proof of a CR-correlated or fresh fix.
export async function followPositionRequest(client: LocalApiClient, deviceId: string, options: PositionFollowupOptions) {
  const { isCurrent, pause, onUpdate, intervalMs = 3000, maxChecks = 20, now=Date.now } = options;
  const startedAt=now(),deadline=startedAt+60_000;
  const readSnapshot = options.readSnapshot ?? ((id: string) => client.snapshot(id));
  const ensureCurrent = () => { if (!isCurrent()) throw new LocalApiError("cancelled"); };
  const baseline = await readSnapshot(deviceId);
  ensureCurrent();
  if (!baseline.device?.connected) throw new LocalApiError("deviceOffline");
  const requestedAt=now();
  await client.requestPosition(deviceId);
  ensureCurrent();
  options.onRequested?.({ baselineId: Math.max(baseline.position?.id ?? 0, baseline.lastValidPosition?.id ?? 0), requestedAt });
  onUpdate(baseline, "waiting");
  let latest = baseline;
  let observed = false;
  for (let check = 0; check < maxChecks && now()<deadline; check++) {
    await pause(Math.min(intervalMs,Math.max(0,deadline-now())));
    ensureCurrent();
    latest = await readSnapshot(deviceId);
    ensureCurrent();
    if (!latest.device?.connected) throw new LocalApiError("deviceOffline");
    const position = latest.position;
    const newRow = position !== null && position.id > (baseline.position?.id ?? 0);
    observed ||= newRow;
    if (newGpsObservation(position,baseline.position?.id??0,requestedAt,now())) {
      onUpdate(latest, "gps");
      return;
    }
    onUpdate(latest, observed ? "waitingNoFix" : "waiting");
  }
  onUpdate(latest, observed ? "noFix" : "noReport");
}
